import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { FieldDefinition, HighlightRule, LoanRecord, PanelField, ToneColors } from '../../types/highlights';
import { styleForTone, toneLabels, toneOrder } from '../../utils/palette';
import { defaultOperatorFor, describeRule, operatorMeta, operatorsByType, ruleMatches } from '../../utils/rules';
interface RulesEditorProps {
  open: boolean;
  focusFieldId: string | null;
  fields: PanelField[];
  catalog: FieldDefinition[];
  byId: Map<string, FieldDefinition>;
  rules: HighlightRule[];
  loan: LoanRecord;
  toneColors: ToneColors;
  onAddRule: (targetFieldId: string) => string;
  onUpdateRule: (ruleId: string, patch: Partial<HighlightRule>) => void;
  onRemoveRule: (ruleId: string) => void;
  onClose: () => void;
}
export function RulesEditor({
  open,
  focusFieldId,
  fields,
  catalog,
  byId,
  rules,
  loan,
  toneColors,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  onClose
}: RulesEditorProps) {
  const [scope, setScope] = useState<string>('all');
  useEffect(() => {
    if (open) setScope(focusFieldId ?? 'all');
  }, [open, focusFieldId]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  const panelFieldDefinitions = useMemo(() => fields.map(field => byId.get(field.fieldId)).filter((definition): definition is FieldDefinition => Boolean(definition)), [fields, byId]);
  const visibleRules = useMemo(() => scope === 'all' ? rules : rules.filter(rule => rule.targetFieldId === scope), [rules, scope]);
  if (!open) return null;
  const scopeTargetId = scope === 'all' ? panelFieldDefinitions[0]?.id : scope;
  return createPortal(<div className="fixed inset-0 z-[9998] flex items-start justify-center bg-slate-900/30 p-6" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-128" data-mp="1ld5l3uw9l6lx">
      <div role="dialog" aria-modal="true" aria-label="Highlight rules" className="mt-10 flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl" data-id="element-129" data-mp="1beyfg87feaz8">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3" data-id="element-130" data-mp="1z4an33n3uazf">
          <div data-id="element-131" data-mp="1ud10kbwurnrr">
            <h2 className="text-sm font-bold text-slate-900" data-id="element-132" data-mp="1ucit0j4qpn4j">Highlight rules</h2>
            <p className="text-[12px] text-slate-500" data-id="element-133" data-mp="1peh95vzhpjs0">
              Color a field based on its own value or any other loan field. The first matching
              rule wins.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close rules editor" className="rounded p-1 text-slate-400 transition hover:bg-slate-50 hover:text-slate-700" data-id="element-134" data-mp="2i2d4aw9ev3mo">
            <XIcon className="h-4 w-4" data-id="element-135" data-mp="2udw2g27apwcf" />
          </button>
        </header>

        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2" data-id="element-136" data-mp="2zbxuhcmp9601">
          <label className="flex items-center gap-2 text-[12px] text-slate-600" data-id="element-137" data-mp="34cbwoe1mdlq5">
            Field
            <select value={scope} onChange={event => setScope(event.target.value)} className="rounded border border-slate-200 px-2 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-138" data-mp="399bxa9fp9w9v">
              <option value="all" data-id="element-139" data-mp="3n1iezzyi4bbf">All panel fields</option>
              {panelFieldDefinitions.map(definition => <option key={definition.id} value={definition.id} data-id="element-140" data-mp="3s1o2d3xqnbnp">
                  {definition.label}
                </option>)}
            </select>
          </label>
          <button type="button" disabled={!scopeTargetId} onClick={() => scopeTargetId && onAddRule(scopeTargetId)} className="inline-flex items-center gap-1.5 rounded-md border border-[#cfe0f5] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#0b5cab] transition hover:bg-[#f3f8ff] disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-141" data-mp="xloycdgiv4zt">
            <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-142" data-mp="emksteo6vtcw" />
            New rule
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-id="element-143" data-mp="9p1fcgzbfkm0">
          {visibleRules.length === 0 ? <p className="py-10 text-center text-[13px] text-slate-500" data-id="element-144" data-mp="nokbebeur5hy">
              No rules yet. Add one to color this field automatically.
            </p> : <ul className="space-y-2" data-id="element-145" data-mp="1z26d0w1wezkd">
              {visibleRules.map(rule => {
            const source = byId.get(rule.sourceFieldId);
            const meta = source ? operatorMeta(source.type, rule.operator) : undefined;
            const inputs = meta?.inputs ?? 0;
            const isMatching = rule.enabled && ruleMatches(rule, loan, byId);
            return <li key={rule.id} className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5" data-id="element-146" data-mp="1sn7ke2yih6re">
                    <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-slate-600" data-id="element-147" data-mp="1xnlppd8qa2lq">
                      <span data-id="element-148" data-mp="2ldpjyu010q02">Color</span>
                      <select aria-label="Field to color" value={rule.targetFieldId} onChange={event => onUpdateRule(rule.id, {
                  targetFieldId: event.target.value
                })} className="max-w-[170px] rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] font-medium text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-149" data-mp="2qaprl9ciy0ta">
                        {panelFieldDefinitions.map(definition => <option key={definition.id} value={definition.id} data-id="element-150" data-mp="3dy30ttp4zs6r">
                            {definition.label}
                          </option>)}
                      </select>
                      <span data-id="element-151" data-mp="34xoecr0fs71o">when</span>
                      <select aria-label="Field to evaluate" value={rule.sourceFieldId} onChange={event => {
                  const next = byId.get(event.target.value);
                  onUpdateRule(rule.id, {
                    sourceFieldId: event.target.value,
                    operator: next ? defaultOperatorFor(next.type) : rule.operator,
                    value: '',
                    value2: undefined
                  });
                }} className="max-w-[170px] rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] font-medium text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-152" data-mp="34wmzebgav0eo">
                        {catalog.map(definition => <option key={definition.id} value={definition.id} data-id="element-153" data-mp="11ocy6fxdsvj2">
                            {definition.label}
                          </option>)}
                      </select>
                      <select aria-label="Condition" value={rule.operator} onChange={event => onUpdateRule(rule.id, {
                  operator: event.target.value as HighlightRule['operator']
                })} className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-154" data-mp="910au3jecjop">
                        {(source ? operatorsByType[source.type] : []).map(option => <option key={option.value} value={option.value} data-id="element-155" data-mp="2jv7d7gjnjjao">
                            {option.label}
                          </option>)}
                      </select>
                      {inputs >= 1 ? <input aria-label="Comparison value" value={rule.value ?? ''} placeholder={meta?.placeholder} onChange={event => onUpdateRule(rule.id, {
                  value: event.target.value
                })} className="w-24 rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-156" data-mp="2ory46ywzvzav" /> : null}
                      {inputs === 2 ? <>
                          <span data-id="element-157" data-mp="3hipw3k7121se">and</span>
                          <input aria-label="Second comparison value" value={rule.value2 ?? ''} placeholder={meta?.placeholder} onChange={event => onUpdateRule(rule.id, {
                    value2: event.target.value
                  })} className="w-24 rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-158" data-mp="3z9mrkajfnzs" />
                        </> : null}
                      {isMatching ? <span className="ml-1 rounded-full bg-[#0b5cab] px-2 py-0.5 text-[10.5px] font-bold text-white" data-id="element-159" data-mp="38bwqs9r5yc1x">
                          Matching now
                        </span> : null}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-3" data-id="element-160" data-mp="33f6b3nj9tih1">
                      <div role="radiogroup" aria-label="Highlight color" className="flex items-center gap-1" data-id="element-161" data-mp="1achrn1jrmofn">
                        {toneOrder.map(tone => {
                    const active = rule.tone === tone;
                    return <button key={tone} type="button" role="radio" aria-checked={active} aria-label={toneLabels[tone]} title={toneLabels[tone]} onClick={() => onUpdateRule(rule.id, {
                      tone
                    })} className={['h-5 w-5 rounded border-2 transition', styleForTone(tone, toneColors).swatch, active ? 'border-slate-900' : 'border-white hover:border-slate-300'].join(' ')} data-id="element-162" data-mp="15dezuissqepm" />;
                  })}
                      </div>

                      <input aria-label="Badge text" value={rule.flag ?? ''} placeholder="Badge text (optional)" onChange={event => onUpdateRule(rule.id, {
                  flag: event.target.value
                })} className="w-44 rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-163" data-mp="e7rb16jv6vu2" />

                      <button type="button" role="switch" aria-checked={rule.enabled} aria-label="Rule enabled" onClick={() => onUpdateRule(rule.id, {
                  enabled: !rule.enabled
                })} className={['relative h-4 w-8 shrink-0 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]', rule.enabled ? 'bg-[#0b5cab]' : 'bg-slate-300'].join(' ')} data-id="element-164" data-mp="16u26k2sm66k0">
                        <span aria-hidden="true" className={['absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', rule.enabled ? 'left-[18px]' : 'left-0.5'].join(' ')} data-id="element-165" data-mp="3cxio86neaca1" />
                      </button>

                      <p className="min-w-0 flex-1 truncate text-[11px] text-slate-500" data-id="element-166" data-mp="2t8qcyckgtbux">
                        {describeRule(rule, byId)}
                      </p>

                      <button type="button" onClick={() => onRemoveRule(rule.id)} aria-label="Delete rule" className="rounded p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-700" data-id="element-167" data-mp="2y60dan9e77xj">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-168" data-mp="2grz4rlrnoebl" />
                      </button>
                    </div>
                  </li>;
          })}
            </ul>}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-2.5" data-id="element-169" data-mp="22yq01yenkgze">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition hover:bg-slate-50" data-id="element-170" data-mp="1xzmumcurg8qw">
            Done
          </button>
        </footer>
      </div>
    </div>, document.body);
}